import {Component, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {MatPaginator} from '@angular/material/paginator';
import {MatSnackBar} from '@angular/material/snack-bar';
import {MatSort, MatSortHeader} from '@angular/material/sort';
import {
    MatCell,
    MatCellDef,
    MatColumnDef,
    MatHeaderCell,
    MatHeaderCellDef,
    MatHeaderRow, MatHeaderRowDef, MatRow, MatRowDef,
    MatTable,
    MatTableDataSource
} from '@angular/material/table';
import {ActivatedRoute, RouterLink} from '@angular/router';
import {catchError, tap} from 'rxjs/operators';
import {Item} from '../../models/item';
import {Observable, Subscription, throwError} from 'rxjs';
import {BreadCrumb} from '../../models/breadcrumb';
import {ExportItem} from '../../models/export-item';
import {
    collection,
    collectionData,
    deleteDoc,
    doc,
    docData,
    DocumentData,
    DocumentReference,
    Firestore,
    orderBy,
    query,
    where
} from '@angular/fire/firestore';
import {BreadcrumbsComponent} from '../breadcrumbs/breadcrumbs.component';
import {ProgressComponent} from '../progress/progress.component';
import {MatCard, MatCardContent, MatCardHeader, MatCardSubtitle, MatCardTitle} from '@angular/material/card';
import {MatFormField} from '@angular/material/form-field';
import {MatLabel, MatOption, MatSelect} from '@angular/material/select';
import {I18nPluralPipe} from '@angular/common';
import {MatButton, MatMiniFabButton} from '@angular/material/button';
import {stringify} from 'csv-stringify/browser/esm/sync';
import {MatInput} from '@angular/material/input';
import {Timestamp} from 'firebase/firestore';

@Component({
    selector: 'app-inventory',
    templateUrl: './facility-items.component.html',
    styleUrls: ['./facility-items.component.css'],
    imports: [
        BreadcrumbsComponent,
        ProgressComponent,
        MatCard,
        MatCardTitle,
        MatCardSubtitle,
        MatCardHeader,
        MatFormField,
        MatLabel,
        MatSelect,
        MatOption,
        MatCardContent,
        MatTable,
        MatSort,
        MatSortHeader,
        MatColumnDef,
        MatHeaderCellDef,
        MatHeaderCell,
        MatCellDef,
        MatCell,
        RouterLink,
        MatHeaderRow,
        MatHeaderRowDef,
        MatRow,
        MatRowDef,
        MatPaginator,
        I18nPluralPipe,
        MatButton,
        MatInput,
        MatMiniFabButton
    ],
    standalone: true
})

export class FacilityItemsComponent implements OnInit, OnDestroy {

    @ViewChild(MatPaginator, {static: true}) paginator: MatPaginator;
    @ViewChild(MatSort, {static: true}) sort: MatSort;
    facilityId = '';
    dataSource: MatTableDataSource<Item>;
    facility: Observable<DocumentData>;
    facilityName = '';
    displayedColumns: string[] = ['customerPartNumber', 'location', 'uniqueItemId', 'itemType', 'eclipseId', 'actions'];
    breadcrumbs: BreadCrumb[];
    itemPluralMapping = {
        '=0': '0 Items',
        '=1': '1 Item',
        other: '# Items'
    };
    statusList: any [] = [
        {value: -1, label: 'All Items'},
        {value: 0, label: 'Available to Ship'},
        {value: 1, label: 'Shipped'},
        {value: 2, label: 'Received'},
        {value: 3, label: 'Available for Requisition'},
        {value: 4, label: 'Requisitioned to Job Site'},
        {value: 5, label: 'Bypassed Receive Workflow'},
        {value: 6, label: 'Bypassed Putaway Workflow'},
    ];

    private subscription: Subscription;
    private facilitySubscription: Subscription;
    private locationSubscription: Subscription;
    private facilityDocument: DocumentReference;

    constructor(private firestore: Firestore,
                private route: ActivatedRoute,
                private snackBar: MatSnackBar) {
    }

    ngOnInit() {
        this.breadcrumbs = [
            {url: ['/'], title: 'Accounts'},
            {url: null, title: 'Facilities'},
            {url: null, title: 'Items'},
        ];

        this.route.paramMap.subscribe(params => {
            this.facilityId = params.get('facilityId');
            const facilityDocRef = doc(this.firestore, 'facilities', this.facilityId);
            this.facilityDocument = facilityDocRef;
            this.facility = docData(facilityDocRef);
            this.facilitySubscription = this.facility.pipe(
                tap(data => {
                    this.facilityName = data.name;
                    this.breadcrumbs[1].url = ['/facilities', data.accountId];
                    this.breadcrumbs[2].title = `${data.name} Items`;
                })
            ).subscribe();
        });
        this.fetchItems(-1);
    }

    deleteItem(id: string) {
        deleteDoc(doc(collection(this.firestore, 'items'), id)).then(() => {
            this.snackBar.open('Item Deleted', null, {duration: 3000});
        }).catch(error => {
            this.snackBar.open('Error Deleting Item: ' + error, null, {duration: 3000});
        });
    }

    ngOnDestroy(): void {
        if (this.subscription) {
            this.subscription.unsubscribe();
        }
        if (this.locationSubscription) {
            this.locationSubscription.unsubscribe();
        }
        if (this.facilitySubscription) {
            this.facilitySubscription.unsubscribe();
        }
    }

    search(event: any) {
        this.dataSource.filter = event.target.value.trim().toLowerCase();
    }

    fetchItems(status: number) {
        status = Number(status);
        const itemsColRef = collection(this.firestore, 'items');
        let itemsQuery = query(itemsColRef);
        if (status !== -1) {
            if (status === 5) {
                itemsQuery = query(itemsQuery, where('flag', '==', 1));
            } else if (status === 6) {
                itemsQuery = query(itemsQuery, where('flag', '==', 2));
            } else {
                itemsQuery = query(itemsQuery, where('status', '==', status));
            }
        }
        const facilityRef = doc(this.firestore, 'facilities', this.facilityId);
        itemsQuery = query(
            itemsQuery,
            where('facility', '==', facilityRef),
            orderBy('customerPartNumber', 'asc')
        );
        this.subscription = collectionData(itemsQuery, {idField: 'id'}).pipe(
            catchError(e => throwError(() => e))
        ).subscribe({
            next: (data: Array<Item & { id: string }>) => {
                this.dataSource = new MatTableDataSource(data);
                this.dataSource.sort = this.sort;
                this.dataSource.paginator = this.paginator;
            },
            error: (error) => {
                console.log(error);
            }
        });
    }

    downloadCsv() {
        const exports: ExportItem[] = [];
        for (const item of this.dataSource.data) {
            const timestamp = item.fifoDate;
            const tmp: ExportItem = {} as ExportItem;
            tmp.tag = item.tag;
            tmp.customerPartNumber = item.customerPartNumber;
            tmp.destination = item.destination;
            tmp.eclipseId = item.eclipseId;
            tmp.facility = item.facilityName;
            if (timestamp instanceof Timestamp) {
                tmp.fifoDate = timestamp.toDate().toISOString();
            }
            tmp.foundDuringLastCycleCount = item.foundDuringLastCycleCount ? 'Yes' : 'No';
            tmp.itemDescription = item.itemDescription;
            tmp.itemType = item.itemType;
            if (!item.locationName && item.status === 4) {
                tmp.location = 'Jobsite';
            } else {
                tmp.location = item.locationName;
            }
            tmp.pull = item.pull;
            tmp.quantity = item.quantity.toString();
            tmp.source = item.source;
            tmp.palletized = item.palletized ? 'Yes' : 'No';
            tmp.palletNumber = item.palletNumber;
            switch (item.status) {
                case 0:
                    tmp.status = 'Available To Ship';
                    break;
                case 1:
                    tmp.status = 'Shipped';
                    break;
                case 2:
                    tmp.status = 'Received';
                    break;
                case 3:
                    tmp.status = 'Available for Requisition';
                    break;
                case 4:
                    tmp.status = 'Requisitioned to Jobsite';
                    break;
            }
            switch (item.flag) {
                case 0:
                    tmp.flag = 'Normal';
                    break;
                case 1:
                    tmp.flag = 'Bypassed Receive workflow';
                    break;
                case 2:
                    tmp.flag = 'Bypassed Putaway workflow';
                    break;
                default:
                    tmp.flag = 'Normal';
                    break;
            }
            exports.push(tmp);
        }
        const csv = stringify(exports, {
            header: true,
            columns: [
                {key: 'tag', header: 'tag'},
                {key: 'customerPartNumber', header: 'customerPartNumber'},
                {key: 'destination', header: 'destination'},
                {key: 'eclipseId', header: 'eclipseId'},
                {key: 'facility', header: 'facility'},
                {key: 'fifoDate', header: 'fifoDate'},
                {key: 'foundDuringLastCycleCount', header: 'foundDuringLastCycleCount'},
                {key: 'itemDescription', header: 'itemDescription'},
                {key: 'itemType', header: 'itemType'},
                {key: 'location', header: 'location'},
                {key: 'pull', header: 'pull'},
                {key: 'quantity', header: 'quantity'},
                {key: 'source', header: 'source'},
                {key: 'palletized', header: 'palletized'},
                {key: 'palletNumber', header: 'palletNumber'},
                {key: 'status', header: 'status'},
                {key: 'flag', header: 'flag'},
            ],
        });
        this.download(`${this.facilityName}-export.csv`, csv);
    }

    download(filename, text) {
        const element = document.createElement('a');
        element.setAttribute('href', 'data:text/csv;charset=utf-8,' + encodeURIComponent(text));
        element.setAttribute('download', filename);
        element.style.display = 'none';
        document.body.appendChild(element);
        element.click();
        document.body.removeChild(element);
    }
}
